import { STORAGE_KEYS } from '~constants'

/**
 * 진입 OTP 를 한 번만 쓰도록 관리한다.
 *
 * OTP 는 서버에서 1회용이다 — `STSignInOTPSerializer.authenticate()` 가 인증에 성공하면
 * redis 에서 즉시 지우고, 같은 값으로 다시 오면 401 `Invalid OTP.` 를 돌려준다.
 *
 * 그런데 진입 URL 은 호스트(SmartThings)가 들고 있다가 웹뷰를 재생성할 때 그대로 다시
 * 연다. 그래서 이미 쓴 OTP 가 URL 에 실린 채 재진입하는 일이 생기고, 그때 그 OTP 를
 * "새 세션 요청" 으로 취급하면 멀쩡한 세션만 날리고 새 세션은 만들지 못한다(DRT-6525).
 *
 * 소비 기록을 하나만 들고 있으면 OTP 가 두 번 발급된 뒤 더 오래된 진입 URL 이 다시 열릴
 * 때 판정을 놓치므로, 최근 몇 개를 함께 들고 있는다.
 */
const SPENT_OTP_HISTORY_SIZE = 5

const readSpentOtps = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SPENT_OTPS)
    if (!raw) {
      return []
    }

    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as string[]) : []
  } catch {
    // storage 접근이 막혔거나 값이 깨진 환경에서는 판단을 보류한다.
    return []
  }
}

const writeSpentOtps = (otps: string[]) => {
  localStorage.setItem(STORAGE_KEYS.SPENT_OTPS, JSON.stringify(otps.slice(-SPENT_OTP_HISTORY_SIZE)))
}

export const isOtpSpent = (otp: string): boolean => readSpentOtps().includes(otp)

/**
 * 표식은 요청을 보내기 전에 남긴다. 응답을 기다리는 동안 웹뷰가 재생성되면 콜백이
 * 실행되지 않아 사후 정리로는 재사용을 막을 수 없기 때문이다.
 */
export const markOtpSpent = (otp: string) => {
  try {
    writeSpentOtps([...readSpentOtps().filter(spent => spent !== otp), otp])
  } catch {
    // 기록에 실패해도 로그인 자체를 막지 않는다. isOtpSpent 가 false 를 돌려주는 것과
    // 같은 상태이므로 기존 동작으로 되돌아갈 뿐이다.
  }
}

/**
 * 응답이 아예 없었다는 것은 서버가 OTP 를 받아보지도 못했다는 뜻이므로 소비됐을 리 없다.
 * 그때만 표식을 되돌려 다음 진입에서 재시도할 수 있게 한다.
 */
export const restoreOtp = (otp: string) => {
  try {
    writeSpentOtps(readSpentOtps().filter(spent => spent !== otp))
  } catch {
    // 복구에 실패해도 Samsung Account 버튼을 통한 로그인 경로는 그대로 남는다.
  }
}
